import { SCENE_PADDING } from "../sceneConstants";
import { CANVAS_W, CANVAS_H } from "./transform";
import type { PxBox, ResizeHandle } from "./geometry";

/**
 * Smart-alignment ("snapping") engine. All math runs in uniform canvas-px space
 * (1920×1080), the same frame as the gesture geometry in `geometry.ts`. Given a
 * live box plus its sibling boxes, it returns a box nudged so its edges/centers
 * line up with siblings, the canvas, or the content safe-area, plus the
 * alignment guide lines to draw.
 *
 * IMPORTANT: this is a *suggestion*, not a lock. The caller keeps the raw,
 * unsnapped box as the source of truth and only ever feeds the raw box in here —
 * so once the pointer travels past the threshold the result is the raw box again
 * and the element releases cleanly (no axis "sticking").
 *
 * Snapping works on axis-aligned bounding boxes (AABBs), so it behaves sensibly
 * for rotated elements too (Figma-style "snap the bounds").
 */

/** A guide line to render, in canvas-px. `axis: "x"` is a vertical line. */
export interface SnapGuide {
    axis: "x" | "y";
    /** Coordinate of the line on its axis (x for vertical, y for horizontal). */
    pos: number;
    /** Segment extent along the cross axis. */
    start: number;
    end: number;
}

/** Snap distance in *screen* px; callers scale it into canvas-px per zoom. */
export const SNAP_SCREEN_PX = 5;

const MIN_PX = 24;
/** Distribution must beat alignment by this much (canvas-px) to win an axis. */
const ALIGN_PREFERENCE = 1;
/** A gap/overlap must exceed this to count, avoiding noise at touching edges. */
const MIN_GAP = 0.5;

interface Aabb {
    left: number;
    top: number;
    right: number;
    bottom: number;
    cx: number;
    cy: number;
}

const rad = (deg: number) => (deg * Math.PI) / 180;

function aabbOf(box: PxBox): Aabb {
    const cx = box.x + box.w / 2;
    const cy = box.y + box.h / 2;
    if (!box.rotation) {
        return {
            left: box.x,
            top: box.y,
            right: box.x + box.w,
            bottom: box.y + box.h,
            cx,
            cy,
        };
    }
    const t = rad(box.rotation);
    const c = Math.abs(Math.cos(t));
    const s = Math.abs(Math.sin(t));
    const halfW = (box.w * c + box.h * s) / 2;
    const halfH = (box.w * s + box.h * c) / 2;
    return {
        left: cx - halfW,
        top: cy - halfH,
        right: cx + halfW,
        bottom: cy + halfH,
        cx,
        cy,
    };
}

/** A line some moving edge can snap to, plus the source's cross-axis extent. */
interface AxisCandidate {
    pos: number;
    from: number;
    to: number;
    /** Canvas edge/center → the guide spans the whole canvas. */
    full: boolean;
}

/** Everything a gesture needs to snap against: alignment lines + sibling AABBs. */
export interface SnapContext {
    x: AxisCandidate[];
    y: AxisCandidate[];
    siblings: Aabb[];
}

/**
 * Build the snap context for a drag: the canvas edges + center, the content
 * safe-area (inner padding) edges, and every sibling's AABB.
 */
export function buildSnapContext(siblings: PxBox[]): SnapContext {
    const x: AxisCandidate[] = [
        { pos: 0, from: 0, to: CANVAS_H, full: true },
        { pos: SCENE_PADDING, from: 0, to: CANVAS_H, full: true },
        { pos: CANVAS_W / 2, from: 0, to: CANVAS_H, full: true },
        { pos: CANVAS_W - SCENE_PADDING, from: 0, to: CANVAS_H, full: true },
        { pos: CANVAS_W, from: 0, to: CANVAS_H, full: true },
    ];
    const y: AxisCandidate[] = [
        { pos: 0, from: 0, to: CANVAS_W, full: true },
        { pos: SCENE_PADDING, from: 0, to: CANVAS_W, full: true },
        { pos: CANVAS_H / 2, from: 0, to: CANVAS_W, full: true },
        { pos: CANVAS_H - SCENE_PADDING, from: 0, to: CANVAS_W, full: true },
        { pos: CANVAS_H, from: 0, to: CANVAS_W, full: true },
    ];

    const aabbs: Aabb[] = [];
    for (const s of siblings) {
        const b = aabbOf(s);
        aabbs.push(b);
        const yRange = { from: b.top, to: b.bottom };
        const xRange = { from: b.left, to: b.right };
        x.push(
            { pos: b.left, ...yRange, full: false },
            { pos: b.cx, ...yRange, full: false },
            { pos: b.right, ...yRange, full: false }
        );
        y.push(
            { pos: b.top, ...xRange, full: false },
            { pos: b.cy, ...xRange, full: false },
            { pos: b.bottom, ...xRange, full: false }
        );
    }
    return { x, y, siblings: aabbs };
}

// ── Alignment (edges & centers line up) ─────────────────────────────────────

interface AlignSnap {
    delta: number;
    cand: AxisCandidate;
}

/** Closest alignment among (probe edge × candidate) pairs within threshold. */
function bestAlign(
    probes: number[],
    cands: AxisCandidate[],
    threshold: number
): AlignSnap | null {
    let best: AlignSnap | null = null;
    for (const probe of probes) {
        for (const cand of cands) {
            const delta = cand.pos - probe;
            if (Math.abs(delta) > threshold) continue;
            if (!best || Math.abs(delta) < Math.abs(best.delta))
                best = { delta, cand };
        }
    }
    return best;
}

function makeGuide(axis: "x" | "y", cand: AxisCandidate, bb: Aabb): SnapGuide {
    if (axis === "x") {
        return {
            axis,
            pos: cand.pos,
            start: cand.full ? 0 : Math.min(bb.top, cand.from),
            end: cand.full ? CANVAS_H : Math.max(bb.bottom, cand.to),
        };
    }
    return {
        axis,
        pos: cand.pos,
        start: cand.full ? 0 : Math.min(bb.left, cand.from),
        end: cand.full ? CANVAS_W : Math.max(bb.right, cand.to),
    };
}

// ── Distribution (equal gaps between elements) ──────────────────────────────

/**
 * One axis viewed abstractly so the equal-spacing logic is written once and
 * reused for both horizontal gaps ("x") and vertical gaps ("y"). "main" is the
 * axis the gap is measured along; "cross" is the band the elements must share.
 */
interface AxisView {
    axis: "x" | "y";
    mainLo: (b: Aabb) => number;
    mainHi: (b: Aabb) => number;
    crossLo: (b: Aabb) => number;
    crossHi: (b: Aabb) => number;
}

const H_VIEW: AxisView = {
    axis: "x",
    mainLo: (b) => b.left,
    mainHi: (b) => b.right,
    crossLo: (b) => b.top,
    crossHi: (b) => b.bottom,
};
const V_VIEW: AxisView = {
    axis: "y",
    mainLo: (b) => b.top,
    mainHi: (b) => b.bottom,
    crossLo: (b) => b.left,
    crossHi: (b) => b.right,
};

interface DistSnap {
    delta: number;
}

/**
 * Equal-spacing snap along one axis: nudges the moving box so it is either
 * centered between its two nearest neighbors, or continues an existing equal gap
 * to the left/right. Only neighbors sharing the moving box's cross-band count.
 * (No on-screen bar — the element edges that drive a gap aren't always the
 * element's *visible* edges, so a measured bar would read as the wrong length.)
 */
function distributionSnap(
    m: Aabb,
    siblings: Aabb[],
    v: AxisView,
    threshold: number
): DistSnap | null {
    const size = v.mainHi(m) - v.mainLo(m);
    const center = (v.mainLo(m) + v.mainHi(m)) / 2;

    // Same-band siblings (they overlap the moving box on the cross axis).
    const band = siblings.filter(
        (s) =>
            Math.min(v.crossHi(m), v.crossHi(s)) -
                Math.max(v.crossLo(m), v.crossLo(s)) >
            MIN_GAP
    );
    const before = band
        .filter((s) => v.mainHi(s) <= center)
        .sort((a, b) => v.mainHi(b) - v.mainHi(a));
    const after = band
        .filter((s) => v.mainLo(s) >= center)
        .sort((a, b) => v.mainLo(a) - v.mainLo(b));
    const L = before[0];
    const R = after[0];

    const targets: number[] = [];
    // 1. Centered between the two nearest neighbors → equal gaps on both sides.
    if (L && R) {
        const gap = (v.mainLo(R) - v.mainHi(L) - size) / 2;
        if (gap >= MIN_GAP) targets.push(v.mainHi(L) + gap);
    }
    // 2. Continue an existing gap on the right (…P · L · [box]).
    if (L && before[1]) {
        const g = v.mainLo(L) - v.mainHi(before[1]);
        if (g >= MIN_GAP) targets.push(v.mainHi(L) + g);
    }
    // 3. Continue an existing gap on the left ([box] · R · Q…).
    if (R && after[1]) {
        const g = v.mainLo(after[1]) - v.mainHi(R);
        if (g >= MIN_GAP) targets.push(v.mainLo(R) - g - size);
    }

    let best: DistSnap | null = null;
    for (const targetLo of targets) {
        const delta = targetLo - v.mainLo(m);
        if (Math.abs(delta) > threshold) continue;
        if (!best || Math.abs(delta) < Math.abs(best.delta)) best = { delta };
    }
    return best;
}

interface AxisPick {
    delta: number;
    align: AlignSnap | null;
    dist: DistSnap | null;
}

/** Pick alignment vs distribution for one axis; alignment wins near-ties. */
function pickAxis(align: AlignSnap | null, dist: DistSnap | null): AxisPick | null {
    if (dist && (!align || Math.abs(dist.delta) + ALIGN_PREFERENCE < Math.abs(align.delta)))
        return { delta: dist.delta, align: null, dist };
    if (align) return { delta: align.delta, align, dist: null };
    return null;
}

const asAlign = (align: AlignSnap | null): AxisPick | null =>
    align ? { delta: align.delta, align, dist: null } : null;

export interface SnapResult {
    box: PxBox;
    guides: SnapGuide[];
}

/** Snap a moved box's edges/center to alignment lines and equal-spacing gaps. */
export function snapMove(
    box: PxBox,
    ctx: SnapContext,
    threshold: number
): SnapResult {
    const bb = aabbOf(box);
    const ax = bestAlign([bb.left, bb.cx, bb.right], ctx.x, threshold);
    const ay = bestAlign([bb.top, bb.cy, bb.bottom], ctx.y, threshold);
    let rx = pickAxis(ax, distributionSnap(bb, ctx.siblings, H_VIEW, threshold));
    let ry = pickAxis(ay, distributionSnap(bb, ctx.siblings, V_VIEW, threshold));

    // Keep equal-spacing on a single axis (the stronger one); the other axis
    // falls back to alignment so the overlay never becomes a wall of bars.
    if (rx?.dist && ry?.dist) {
        if (Math.abs(rx.delta) <= Math.abs(ry.delta)) ry = asAlign(ay);
        else rx = asAlign(ax);
    }

    const nb: PxBox = {
        ...box,
        x: box.x + (rx?.delta ?? 0),
        y: box.y + (ry?.delta ?? 0),
    };
    const nbb = aabbOf(nb);
    const guides: SnapGuide[] = [];
    // A distribution pick moves the box but draws no line (an align pick does).
    if (rx?.align) guides.push(makeGuide("x", rx.align.cand, nbb));
    if (ry?.align) guides.push(makeGuide("y", ry.align.cand, nbb));
    return { box: nb, guides };
}

// ── Resize (snap dragged edges to lines or matching sibling sizes) ───────────

const normalizeRot = (r: number) => ((r % 360) + 360) % 360;

interface EdgeSnap {
    target: number;
    delta: number;
    align: AxisCandidate | null;
}

/**
 * Best target for a dragged edge: either an alignment line, or a position that
 * makes the box's size on this axis equal a sibling's (match-size). `anchor` is
 * the fixed opposite edge; `high` is true when dragging the right/bottom edge.
 * A match-size snap nudges the edge but draws no line (only alignment does).
 */
function snapEdge(
    edge: number,
    anchor: number,
    high: boolean,
    cands: AxisCandidate[],
    sizes: number[],
    threshold: number
): EdgeSnap | null {
    let best: EdgeSnap | null = null;
    const consider = (target: number, align: AxisCandidate | null) => {
        const delta = target - edge;
        if (Math.abs(delta) > threshold) return;
        if (!best || Math.abs(delta) < Math.abs(best.delta))
            best = { target, delta, align };
    };
    for (const c of cands) consider(c.pos, c);
    for (const size of sizes) consider(high ? anchor + size : anchor - size, null);
    return best;
}

function placeAspect(box: PxBox, handle: ResizeHandle, w: number, h: number): PxBox {
    const x = handle.includes("w") ? box.x + box.w - w : box.x;
    const y = handle.includes("n") ? box.y + box.h - h : box.y;
    return { ...box, x, y, w, h };
}

/**
 * Snap the dragged edge(s) of a resize to alignment lines or matching sibling
 * sizes. Only axis-aligned (unrotated) boxes are snapped — edge math for a
 * rotated box isn't meaningful. When `aspect` is set (Shift on a corner) a
 * single axis snaps and the other is derived from the ratio, so the lock holds.
 */
export function snapResize(
    box: PxBox,
    handle: ResizeHandle,
    ctx: SnapContext,
    threshold: number,
    aspect = false
): SnapResult {
    if (normalizeRot(box.rotation) !== 0) return { box, guides: [] };

    const widthSizes = ctx.siblings.map((s) => s.right - s.left);
    const heightSizes = ctx.siblings.map((s) => s.bottom - s.top);

    const sx = handle.includes("e")
        ? snapEdge(box.x + box.w, box.x, true, ctx.x, widthSizes, threshold)
        : handle.includes("w")
          ? snapEdge(box.x, box.x + box.w, false, ctx.x, widthSizes, threshold)
          : null;
    const sy = handle.includes("s")
        ? snapEdge(box.y + box.h, box.y, true, ctx.y, heightSizes, threshold)
        : handle.includes("n")
          ? snapEdge(box.y, box.y + box.h, false, ctx.y, heightSizes, threshold)
          : null;

    if (aspect) return snapResizeAspect(box, handle, sx, sy);

    const nb: PxBox = { ...box };
    const guides: SnapGuide[] = [];

    if (sx) {
        if (handle.includes("e")) nb.w = Math.max(MIN_PX, nb.w + sx.delta);
        else {
            nb.x += sx.delta;
            nb.w = Math.max(MIN_PX, nb.w - sx.delta);
        }
        if (sx.align) guides.push(makeGuide("x", sx.align, aabbOf(nb)));
    }
    if (sy) {
        if (handle.includes("s")) nb.h = Math.max(MIN_PX, nb.h + sy.delta);
        else {
            nb.y += sy.delta;
            nb.h = Math.max(MIN_PX, nb.h - sy.delta);
        }
        if (sy.align) guides.push(makeGuide("y", sy.align, aabbOf(nb)));
    }

    return { box: nb, guides };
}

/** Aspect-locked resize: snap whichever axis is closer, derive the other. */
function snapResizeAspect(
    box: PxBox,
    handle: ResizeHandle,
    sx: EdgeSnap | null,
    sy: EdgeSnap | null
): SnapResult {
    const ratio = box.w / box.h;
    const useX = sx && (!sy || Math.abs(sx.delta) <= Math.abs(sy.delta));

    if (useX && sx) {
        const w = Math.max(
            MIN_PX,
            handle.includes("e") ? sx.target - box.x : box.x + box.w - sx.target
        );
        const nb = placeAspect(box, handle, w, w / ratio);
        return {
            box: nb,
            guides: sx.align ? [makeGuide("x", sx.align, aabbOf(nb))] : [],
        };
    }
    if (sy) {
        const h = Math.max(
            MIN_PX,
            handle.includes("s") ? sy.target - box.y : box.y + box.h - sy.target
        );
        const nb = placeAspect(box, handle, h * ratio, h);
        return {
            box: nb,
            guides: sy.align ? [makeGuide("y", sy.align, aabbOf(nb))] : [],
        };
    }
    return { box, guides: [] };
}
